import React, { useEffect, useState } from 'react';

import Popup from '../../../common/Popup/Popup.module';
import Spinner from '../../../common/Spinner';

const MAX_NAME_LENGTH = 120;

interface SCIExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  filterLabels: string[];
  totalCount: number;
  isExporting: boolean;
  onConfirm: (campaign: boolean, name: string | null) => void;
}

const SCIExportModal: React.FC<SCIExportModalProps> = ({ isOpen, onClose, filterLabels, totalCount, isExporting, onConfirm }) => {
  const [campaign, setCampaign] = useState(false);
  const [name, setName] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setCampaign(false);
      setName('');
      setSubmitted(false);
    }
  }, [isOpen]);

  const nameError = campaign && submitted && name.trim() === '' ? 'Donnez un nom à la campagne.' : null;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (campaign && name.trim() === '') {return;}
    onConfirm(campaign, campaign ? name.trim() : null);
  };

  return (
    <Popup isOpen={isOpen} onClose={isExporting ? undefined : onClose} title={<span>Exporter les SCI</span>} additionalClass="w-full max-w-lg">
      <form className="space-y-4 text-sm" onSubmit={handleSubmit}>
        <div>
          <div className="mb-1 font-semibold text-gray-700">
            {totalCount.toLocaleString()} SCI exportée{totalCount > 1 ? 's' : ''}, avec les filtres :
          </div>
          {filterLabels.length > 0 ? (
            <ul className="list-inside list-disc text-gray-700">
              {filterLabels.map((label) => <li key={label}>{label}</li>)}
            </ul>
          ) : (
            <p className="text-gray-600">Aucun filtre : toutes les SCI de vos copropriétés.</p>
          )}
          <p className="mt-1 text-xs text-gray-500">Pour changer les filtres, fermez cette fenêtre et ajustez-les au-dessus de la liste.</p>
        </div>

        <label className="flex items-center gap-2">
          <input type="checkbox" checked={campaign} onChange={(e) => setCampaign(e.target.checked)} disabled={isExporting} />
          <span>Cet export sert à une campagne</span>
        </label>

        {campaign && (
          <div className="flex flex-col">
            <label className="text-xs text-gray-600" htmlFor="sci-export-campaign-name">Nom de la campagne</label>
            <input
              id="sci-export-campaign-name"
              className={`rounded border px-2 py-1 ${nameError ? 'border-red-500' : ''}`}
              value={name}
              maxLength={MAX_NAME_LENGTH}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex. : Courrier gestion locative novembre"
              aria-invalid={!!nameError}
              disabled={isExporting}
              autoFocus
            />
            {nameError && <p role="alert" className="mt-0.5 text-xs text-red-600">{nameError}</p>}
          </div>
        )}

        <p className="text-xs text-gray-500">L'export est gardé dans l'historique des exports, avec ses filtres et son fichier.</p>

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={isExporting} className="rounded border border-gray-400 px-3 py-2 text-gray-700 hover:bg-gray-100 disabled:opacity-50">
            Annuler
          </button>
          <button
            type="submit"
            disabled={isExporting || totalCount === 0}
            aria-busy={isExporting}
            className="flex items-center rounded bg-rgpd-green px-3 py-2 text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isExporting ? <><Spinner size="sm" className="mr-2" /> Export...</> : 'Exporter'}
          </button>
        </div>
      </form>
    </Popup>
  );
};

export default SCIExportModal;
